import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Heart, Target, Shield, Users, Star, ArrowRight, Megaphone, Volume2 } from "lucide-react";
import Marquee from "react-fast-marquee";

const About = () => {
  useEffect(() => {
    document.title = "About Us | Soar Solutions Youth Center";
  }, []);

  return (
    <div data-testid="about-page" className="bg-white">

      {/* Hero */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_soaryouth/artifacts/e6ttuge9_IMG_9869%20%281%29.jpeg"
            alt="SOAR Solutions Feed The Deserved volunteers"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-soar-blue via-soar-blue/90 to-soar-blue/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-soar-blue via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-3xl space-y-6 opacity-0 animate-fade-up" style={{ animationFillMode: 'forwards' }}>
            <span className="inline-block border border-soar-teal/40 rounded-full px-4 py-1.5 text-xs font-semibold tracking-widest text-soar-teal uppercase">
              Our Story
            </span>
            <h1 className="font-heading text-6xl sm:text-7xl lg:text-8xl leading-none tracking-tight text-white">
              It all started with a <span className="text-soar-gold">passion for change.</span>
            </h1>
            <p className="text-xl text-white/70 max-w-2xl leading-relaxed">
              At Soar Solutions, you are the reason we exist. We left our old paths to empower youth,
              provide mentorship, and create opportunities for growth.
            </p>
          </div>
        </div>
      </section>

      {/* Marquee Strip */}
      <div className="py-5 bg-[#212163]">
        <Marquee speed={50} gradient={false}>
          {["SOLUTIONS", "OPPORTUNITIES", "ADVOCACY", "RESOURCES", "S.O.A.R."].map((word) => (
            <span key={word} className="font-heading text-4xl text-[#ddb64d]/20 mx-12 tracking-wider">{word}</span>
          ))}
        </Marquee>
      </div>

      {/* Megaphone Section */}
      <section className="pt-8 pb-16 bg-white" data-testid="megaphone-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="card-elevated rounded-3xl p-12">
            <Megaphone className="w-14 h-14 mx-auto mb-6 text-soar-gold" aria-hidden="true" />
            <h2 className="font-heading text-4xl sm:text-5xl text-soar-blue tracking-tight mb-6">
              You are a Megaphone for Your Future
            </h2>
            <p className="text-soar-blue/50 text-lg leading-relaxed max-w-2xl mx-auto">
              Let us help you elevate your life, inspire your dreams, and ensure no one is left behind.
              We will guide you to become the best version of yourself, because you don't know what's
              possible until someone shows you, so let it be us!
            </p>
          </div>
        </div>
      </section>

      {/* Scrolling Ribbon */}
      <div className="py-5 bg-soar-blue">
        <Marquee speed={40} gradient={false}>
          {["SOLUTIONS", "OPPORTUNITIES", "ADVOCACY", "RESOURCES", "S.O.A.R."].map((w) => (
            <span key={w} className="font-heading text-4xl text-soar-gold/20 mx-12 tracking-wider">{w}</span>
          ))}
        </Marquee>
      </div>

      {/* Vision & Goal */}
      <section className="py-24 bg-white" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="relative rounded-3xl overflow-hidden group">
              <div className="absolute inset-0 bg-soar-teal" />
              <div className="relative z-10 p-10 space-y-5">
                <div className="w-14 h-14 rounded-2xl bg-soar-blue/15 flex items-center justify-center">
                  <Target className="w-7 h-7 text-white" aria-hidden="true" />
                </div>
                <h2 className="font-heading text-4xl text-soar-blue tracking-tight">Our Vision</h2>
                <p className="text-white/80 leading-relaxed text-lg">
                  To create a community where emancipated youth have access to safe and stable housing,
                  while also providing comprehensive life skills training and empowering resources for all
                  young individuals, enabling them to achieve self-sufficiency in their lives.
                </p>
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden group">
              <div className="absolute inset-0 bg-soar-gold" />
              <div className="relative z-10 p-10 space-y-5">
                <div className="w-14 h-14 rounded-2xl bg-soar-blue/20 flex items-center justify-center">
                  <Volume2 className="w-7 h-7 text-[#0a0a2e]" aria-hidden="true" />
                </div>
                <h2 className="font-heading text-4xl text-[#0a0a2e] tracking-tight">Our Goal</h2>
                <p className="text-[#0a0a2e]/70 leading-relaxed text-lg">
                  To help young people become self-reliant and confident individuals who are capable of making
                  positive contributions to society, through our commitment to providing Solutions, Opportunities,
                  Advocacy, and Resources.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-soar-teal text-xs font-semibold tracking-widest uppercase">What Drives Us</span>
            <h2 className="font-heading text-5xl md:text-6xl text-soar-blue tracking-tight mt-3">
              Our Core Values
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Heart, title: "Compassion", desc: "We lead with empathy and understanding, meeting youth where they are." },
              { icon: Users, title: "Community", desc: "We believe in the power of connection and building strong support networks." },
              { icon: Shield, title: "Safe Space", desc: "We provide environments where youth feel secure to be themselves." },
              { icon: Star, title: "Empowerment", desc: "We equip youth with tools to think, build, and dream their future." },
            ].map((v) => (
              <div key={v.title} className="card-elevated rounded-3xl p-8 text-center group">
                <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-soar-teal/10 flex items-center justify-center group-hover:bg-soar-teal/20 transition-colors duration-300">
                  <v.icon className="w-8 h-8 text-soar-teal" aria-hidden="true" />
                </div>
                <h3 className="font-heading text-xl text-soar-blue mb-3">{v.title}</h3>
                <p className="text-soar-blue/50 text-sm">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-24 bg-white" data-testid="what-we-do">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-6 space-y-6">
              <span className="text-soar-gold text-xs font-semibold tracking-widest uppercase">Our Focus Areas</span>
              <h2 className="font-heading text-5xl md:text-6xl text-soar-blue tracking-tight">
                We Can Help With...
              </h2>
              <div className="space-y-4 mt-8">
                {[
                  { num: "01", title: "Mentorship", desc: "One-on-one guidance from experienced adults who help youth navigate life's challenges." },
                  { num: "02", title: "Life Skills", desc: "Practical workshops on financial literacy, job readiness, communication, and more." },
                  { num: "03", title: "Career Development", desc: "Resume building, interview skills, and real-world job preparation." },
                  { num: "04", title: "Education Support", desc: "College prep, tutoring, scholarship assistance, and HBCU connections." },
                  { num: "05", title: "Community Service", desc: "Meaningful volunteer opportunities that build leadership and compassion." },
                ].map((item) => (
                  <div key={item.num} className="flex gap-5 py-4 border-b border-white/5 group">
                    <div className="w-12 h-12 rounded-xl bg-soar-teal/10 flex items-center justify-center flex-shrink-0 group-hover:bg-soar-teal/20 transition-colors">
                      <span className="font-heading text-soar-teal text-sm">{item.num}</span>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-soar-blue mb-1">{item.title}</h3>
                      <p className="text-soar-blue/50 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_soaryouth/artifacts/5r9oc224_IMG_0699.jpeg?v=2"
                  alt="SOAR Solutions team at community event"
                  className="rounded-3xl w-full h-[550px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-soar-blue/60 via-transparent to-transparent rounded-3xl" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 relative overflow-hidden" data-testid="about-cta">
        <div className="absolute inset-0 bg-soar-gold" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-5xl md:text-6xl text-[#0a0a2e] tracking-tight mb-6">
            The Community Would Love to Hear From You
          </h2>
          <p className="text-[#0a0a2e]/60 mb-10 max-w-2xl mx-auto text-lg">
            Whether you want to participate in our programs, volunteer your time, or support us financially,
            there's a place for you in the SOAR family.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/programs" data-testid="about-programs-btn">
              <Button className="rounded-full bg-white hover:bg-white/90 text-soar-blue px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
                Explore Programs <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/donate" data-testid="about-donate-btn">
              <Button className="rounded-full bg-soar-blue/20 hover:bg-white/30 text-[#0a0a2e] border border-[#0a0a2e]/20 px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
                <Heart className="w-5 h-5 mr-2" /> Support Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
